import { IMessagePayload } from '@/types/sw';
import { registerSW } from 'virtual:pwa-register';
import { isApp } from './business';
import { swReadyState } from '@/service/secureMedia';

const DEFAULT_SW_MESSAGE_TIMEOUT_MS = 3000;

/**
 * 向 Service Worker 发送消息并等待响应。
 *
 * 与旧版相比：
 * - 不再无脑 `await navigator.serviceWorker.ready`（SW 永不激活时会挂死）；
 *   改为先看 `swReadyState.isReady`，未就绪直接 reject 让上层走降级
 * - 控制器缺失时也直接 reject
 *
 * @param message 要发送的消息对象
 * @param timeout 等待响应的超时时间，默认 3000ms
 */
export const sendMessageToSW = async <T1 extends object, T2 = void>(
    message: T1,
    timeout: number = DEFAULT_SW_MESSAGE_TIMEOUT_MS
): Promise<T2> => {

    if ( typeof navigator === 'undefined' || !( 'serviceWorker' in navigator )) {

        throw new Error( 'Service Worker is not supported.' );

    }

    if ( !swReadyState.isReady ) {

        throw new Error( 'Service Worker is not ready.' );

    }

    const controller = navigator.serviceWorker.controller;
    if ( !controller ) {

        throw new Error( 'Service Worker controller not available.' );

    }

    return new Promise(( resolve, reject ) => {

        const messageChannel = new MessageChannel();

        const timer = setTimeout(() => {

            messageChannel.port2.close();
            reject( new Error( 'Service Worker message timed out.' ));

        }, timeout );

        messageChannel.port2.onmessage = ( event ) => {

            clearTimeout( timer );
            messageChannel.port2.close();
            if ( event.data?.error ) {

                reject( event.data.error );

            } else {

                resolve( event.data as T2 );

            }

        };

        controller.postMessage( message, [messageChannel.port1] );

    });

};

/**
 * 注册一个处理器，用于监听从 Service Worker 发来的消息。
 *
 * 注意：本应用同时使用 SW → 主线程 的"自顶向下广播"（如 `SW_READY`，无 ports）
 * 与"请求-响应"两种通道（`messageClient` 携带 MessagePort）。当 `event.ports`
 * 为空时跳过回包，避免冗余日志噪音。
 */
export const registerMessageHandler = ( handler: ( message: IMessagePayload ) => Promise<unknown> | unknown ): void => {

    if ( !( 'serviceWorker' in navigator )) {

        return;

    }

    navigator.serviceWorker.addEventListener( 'message', ( event ) => {

        Promise.resolve( handler( event.data )).then(( res ) => {

            for ( const port of event.ports ) {

                port.postMessage( res );

            }

        }).catch( console.error );

    });

};

/**
 * 注册 Service Worker。
 *
 * 与旧版本相比，去掉了 `onRegisteredSW` 内的 `location.reload()`：
 * - 旧逻辑在注册成功但 `navigator.serviceWorker.controller` 仍为 null 时无条件 reload，
 *   遇到 SW 文件 404 / scope 冲突 / quota 满 / activate 阻塞等场景会陷入
 *   "reload → 注册成功仍无 controller → reload" 的死循环
 * - 新版本完全依赖 `swReadyState` 的三条独立判定路径
 *   （`SW_READY` 广播 / `controllerchange` 监听 / 超时）来决定可用性
 *   并由 `main.ts` 调用方在 reject 时 `swReadyState.markUnavailable()` 启用降级
 */
export const registerServiceWorker = (): Promise<void> => {

    if ( isApp || location.search.includes( 'force_sw_fallback' )) {

        return Promise.resolve();

    }

    return new Promise(( resolve, reject ) => {

        const updateSW = registerSW({

            async onNeedRefresh() {

                await updateSW( true );

            },
            onRegisteredSW( scriptUrl, registration ) {

                console.info( 'Service Worker registered successfully.', scriptUrl, registration );
                resolve();

            },
            onRegisterError( error ) {

                console.error( 'Service Worker registration failed:', error );
                reject( error );

            },
        });

    });

};
